import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageShell from '../components/ui/PageShell';
import ResponsiveTable from '../components/ui/ResponsiveTable';
import Modal from '../components/ui/Modal';
import FormField from '../components/ui/FormField';
import Button from '../components/ui/Button';
import { Globals } from '../globalfunctions.js';
import { apiClient } from '../utils/apiClient';
import { formatMoney, SUPPORTED_CURRENCIES, DEFAULT_CURRENCY } from '../utils/currency';
import './Subscriptions.css';

const CYCLES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annually', label: 'Annually' }
];

const STATUSES = ['active', 'paused', 'cancelled'];

const EMPTY_FORM = {
  user_id: '',
  description: '',
  product_type: 'Hosting',
  recurring_amount: '',
  unit_cost: '0',
  currency: DEFAULT_CURRENCY,
  tax_rate: '20',
  billing_cycle: 'monthly',
  next_due_date: '',
  ends_on: '',
  status: 'active',
  notes: ''
};

const customerLabel = (customer) => {
  if (!customer) return '—';
  return customer.companyName || [customer.first_name, customer.last_name].filter(Boolean).join(' ') || customer.email;
};

function Subscriptions() {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [showCancelled, setShowCancelled] = useState(false);

  useEffect(() => {
    if (Globals.LoginTime === null) {
      localStorage.setItem('redirectAfterLogin', window.location.pathname);
      navigate('/login');
    }
  }, [navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const [subs, users] = await Promise.all([
        apiClient.get('/api/subscriptions'),
        apiClient.post('/api/users/findall', {})
      ]);
      setSubscriptions(subs);
      setCustomers(users);
    } catch (error) {
      console.error('Error loading subscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (subscription) => {
    setForm({
      user_id: subscription.user_id,
      description: subscription.description,
      product_type: subscription.product_type,
      recurring_amount: subscription.recurring_amount,
      unit_cost: subscription.unit_cost,
      currency: subscription.currency,
      tax_rate: subscription.tax_rate,
      billing_cycle: subscription.billing_cycle,
      next_due_date: subscription.next_due_date || '',
      ends_on: subscription.ends_on || '',
      status: subscription.status,
      notes: subscription.notes || ''
    });
    setEditingId(subscription.id);
    setFormError('');
    setShowForm(true);
  };

  const setField = (name) => (e) => setForm((prev) => ({ ...prev, [name]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.user_id || !form.description || form.recurring_amount === '' || !form.next_due_date) {
      setFormError('Customer, description, amount, and next due date are all required.');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      const payload = { ...form, ends_on: form.ends_on || null };
      if (editingId) {
        await apiClient.put(`/api/subscriptions/${editingId}`, payload);
      } else {
        await apiClient.post('/api/subscriptions', payload);
      }
      setShowForm(false);
      await load();
    } catch (error) {
      console.error('Error saving subscription:', error);
      setFormError(error.message || 'Could not save this subscription.');
    } finally {
      setSaving(false);
    }
  };

  const cancelledCount = subscriptions.filter((row) => row.status === 'cancelled').length;
  const visibleSubscriptions = showCancelled
    ? subscriptions
    : subscriptions.filter((row) => row.status !== 'cancelled');

  const columns = [
    { key: 'description', label: 'Description', sortable: true },
    { key: 'customer', label: 'Customer', render: (row) => customerLabel(row.customer) },
    { key: 'product_type', label: 'Type', sortable: true },
    {
      key: 'recurring_amount',
      label: 'Amount',
      sortable: true,
      sortValue: (row) => Number(row.recurring_amount),
      render: (row) => formatMoney(row.recurring_amount, row.currency)
    },
    { key: 'billing_cycle', label: 'Cycle', sortable: true, render: (row) => CYCLES.find((c) => c.value === row.billing_cycle)?.label || row.billing_cycle },
    {
      key: 'next_due_date',
      label: 'Next Invoice',
      sortable: true,
      render: (row) => row.next_due_date ? new Date(row.next_due_date).toLocaleDateString() : '—'
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => <span className={`subscription-status subscription-status-${row.status}`}>{row.status}</span>
    },
    {
      key: 'actions',
      label: '',
      render: (row) => <Button variant="secondary" onClick={(e) => { e.stopPropagation(); openEdit(row); }}>Edit</Button>
    }
  ];

  return (
    <PageShell>
      <div className="subscriptions-header">
        <div>
          <h1>Subscriptions</h1>
          <p className="subscriptions-intro">Recurring charges are invoiced automatically 14 days before each due date.</p>
        </div>
        <Button onClick={openNew}>New Subscription</Button>
      </div>

      <div className="subscriptions-status-filter">
        <label className="subscriptions-status-filter-toggle">
          <input
            type="checkbox"
            checked={showCancelled}
            onChange={(e) => setShowCancelled(e.target.checked)}
          />
          Show cancelled subscriptions
          {!showCancelled && cancelledCount > 0 && <span className="subscriptions-status-filter-count">({cancelledCount} hidden)</span>}
        </label>
      </div>

      {loading ? (
        <p>Loading subscriptions...</p>
      ) : (
        <ResponsiveTable columns={columns} rows={visibleSubscriptions} rowKey="id" emptyMessage="No subscriptions set up yet." />
      )}

      {showForm && (
        <Modal open onClose={() => setShowForm(false)} title={editingId ? 'Edit Subscription' : 'New Subscription'} size="large">
          <form className="subscription-form" onSubmit={save}>
            <FormField id="user_id" label="Customer" required>
              <select id="user_id" value={form.user_id} onChange={setField('user_id')}>
                <option value="">Select a customer</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>{customerLabel(c)} — {c.email}</option>
                ))}
              </select>
            </FormField>

            <FormField id="description" label="Description" required>
              <input id="description" value={form.description} onChange={setField('description')} placeholder="Business Hosting - 10GB" />
            </FormField>

            <FormField id="product_type" label="Type">
              <input id="product_type" value={form.product_type} onChange={setField('product_type')} placeholder="Hosting" />
            </FormField>

            <div className="subscription-form-row">
              <FormField id="recurring_amount" label="Amount" required>
                <input id="recurring_amount" type="number" step="0.01" min="0" value={form.recurring_amount} onChange={setField('recurring_amount')} />
              </FormField>
              <FormField id="unit_cost" label="Cost to us">
                <input id="unit_cost" type="number" step="0.01" min="0" value={form.unit_cost} onChange={setField('unit_cost')} />
              </FormField>
            </div>

            <div className="subscription-form-row">
              <FormField id="currency" label="Currency">
                <select id="currency" value={form.currency} onChange={setField('currency')}>
                  {Object.keys(SUPPORTED_CURRENCIES).map((code) => (
                    <option key={code} value={code}>{code}</option>
                  ))}
                </select>
              </FormField>
              <FormField id="tax_rate" label="VAT / Tax (%)">
                <input id="tax_rate" type="number" step="0.01" min="0" max="100" value={form.tax_rate} onChange={setField('tax_rate')} />
              </FormField>
              <FormField id="billing_cycle" label="Billing Cycle">
                <select id="billing_cycle" value={form.billing_cycle} onChange={setField('billing_cycle')}>
                  {CYCLES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </FormField>
            </div>

            <div className="subscription-form-row">
              <FormField id="next_due_date" label="Next Invoice Date" required>
                <input id="next_due_date" type="date" value={form.next_due_date} onChange={setField('next_due_date')} />
              </FormField>
              <FormField id="ends_on" label="Ends On (optional)">
                <input id="ends_on" type="date" value={form.ends_on} onChange={setField('ends_on')} />
              </FormField>
              <FormField id="status" label="Status">
                <select id="status" value={form.status} onChange={setField('status')}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </FormField>
            </div>

            <FormField id="notes" label="Notes">
              <input id="notes" value={form.notes} onChange={setField('notes')} />
            </FormField>

            {formError && <div className="subscription-form-error">{formError}</div>}

            <div className="subscription-form-actions">
              <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
            </div>
          </form>
        </Modal>
      )}
    </PageShell>
  );
}

export default Subscriptions;
